package ai.wagemix.logic.analytics

import ai.wagemix.data.WMCache
import ai.wagemix.data.touchesUser
import ai.wagemix.logic.WMBaseDataGateway
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.entitledPlanOf
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlin.time.Instant

/**
 * What the analytics profile of the signed-in user says about them (planning/analytics-plan.md §3, profile
 * properties). The site sends it to OpenPanel as a profile update whenever it changes.
 *
 * Values are equal when every field is, so a changed profile is told apart from a repeated one with `!=`.
 */
interface WMAnalyticsProfile {

    /** The Firebase uid, OpenPanel's profile id. */
    val profileId: String

    /** The account email, kept only on the profile so the user can be contacted about problems. */
    val email: String

    /** The registration first name, or null when not known yet. */
    val firstName: String?

    /** The registration last name, or null when not known yet. */
    val lastName: String?

    /** The plan the user may use now; [WMSubscriptionType.None] until a subscription is known and once it lapsed. */
    val plan: WMSubscriptionType

    /** When the account was created, or null when the identity provider did not say. */
    val signedUpAt: Instant?

    /** Whether the account email is verified. */
    val emailVerified: Boolean

    /** Anchors [of]. */
    companion object {

        /**
         * The analytics profile of [user].
         *
         * @param user a signed-in user from the cache
         * @return the profile
         */
        fun of(user: WMCache.Value.User): WMAnalyticsProfile {
            return WMAnalyticsProfileData(
                profileId = user.uid,
                email = user.session.email,
                firstName = user.userData?.firstName?.ifBlank { null },
                lastName = user.userData?.lastName?.ifBlank { null },
                plan = entitledPlanOf(status = user.userData?.status, plan = user.subscription?.plan),
                signedUpAt = user.session.createdAt,
                emailVerified = user.session.emailVerified,
            )
        }
    }
}

private data class WMAnalyticsProfileData(
    override val profileId: String,
    override val email: String,
    override val firstName: String?,
    override val lastName: String?,
    override val plan: WMSubscriptionType,
    override val signedUpAt: Instant?,
    override val emailVerified: Boolean,
): WMAnalyticsProfile

/**
 * The analytics profile of the signed-in user, or null while nobody is signed in: the current value first, then one
 * value per change. A cache write that changes none of the profile's fields emits nothing.
 */
fun WMBaseDataGateway.analyticsProfileFlow(): Flow<WMAnalyticsProfile?> {
    return cacheUpdateFlow
        .filter { it.touchesUser() }
        .map { retrieveActiveUser()?.let { user -> WMAnalyticsProfile.of(user) } }
        .onStart { emit(retrieveActiveUser()?.let { user -> WMAnalyticsProfile.of(user) }) }
        .distinctUntilChanged()
}
